import {
	Alert,
	Autocomplete,
	Box,
	Card,
	CardContent,
	CardHeader,
	Grid,
} from "@mui/material";
import debounce from "@mui/utils/debounce";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	ConfirmButton,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import {TextField} from "@variocube/app-ui";
import React, {useState} from "react";
import {useAsync} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {Creditor, useCreditorApi} from "../../../api";
import {LabeledText} from "../../../component/labeled-text";
import {useLocalization} from "../../../i18n";
import {useNumericParam} from "../../../utils/useParam";

export function CreditorMerge() {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {getCreditor, queryCreditors, mergeCreditor} = useCreditorApi();
	const sourceId = useNumericParam("sourceId");

	const {loading, error, result: source} = useAsync(getCreditor, [sourceId]);

	const [search, setSearch] = useState("");
	const [target, setTarget] = useState<Creditor | null>(null);
	const [merging, setMerging] = useState(false);

	const handleSearchInput = debounce(setSearch, 500);

	const {result: options} = useAsync(async () => {
		const result = await queryCreditors({companyName: search, size: 25});
		return (result.content ?? []).filter(c => c.id !== sourceId);
	}, [search]);

	async function handleMerge() {
		if (target) {
			setMerging(true);
			try {
				await mergeCreditor(target.id, sourceId);
				navigate(`/admin/creditors/${target.id}`);
			} finally {
				setMerging(false);
			}
		}
	}

	return (
		<View loading={loading} error={error}>
			<Breadcrumbs>
				<BreadcrumbItem>{t("navigation.admin")}</BreadcrumbItem>
				<BreadcrumbLink component={Link} to={"/admin/creditors"}>{t("creditors.plural")}</BreadcrumbLink>
				<BreadcrumbLink component={Link} to={`/admin/creditors/${sourceId}`}>{source?.company.companyName}</BreadcrumbLink>
				<BreadcrumbItem>{t("creditors.merge.title")}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader title={t("creditors.merge.title")} />
			<Alert severity="info" sx={{mb: 3}}>
				{t("creditors.merge.info")}
			</Alert>
			<Box>
			<Grid container spacing={3}>
				<Grid item xs={12} md={5}>
					<Card>
						<CardHeader title={t("creditors.merge.source")} />
						<CardContent>
							{source && <CreditorDetails creditor={source} />}
						</CardContent>
					</Card>
				</Grid>
				<Grid item xs={12} md={2} sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
					<Box sx={{fontSize: 32}}>→</Box>
				</Grid>
				<Grid item xs={12} md={5}>
					<Card>
						<CardHeader title={t("creditors.merge.target")} />
						<CardContent>
							<Autocomplete
								onInputChange={(_, v) => handleSearchInput(v)}
								renderInput={params => <TextField {...params} label={t("creditors.merge.selectTarget")} />}
								options={options ?? []}
								value={target}
								onChange={(_, v) => setTarget(v)}
								getOptionLabel={o => o.company.companyName ?? ""}
								isOptionEqualToValue={(option, value) => option.id === value.id}
								sx={{mb: 2}}
							/>
							{target && <CreditorDetails creditor={target} />}
						</CardContent>
					</Card>
				</Grid>
			</Grid>
			</Box>
			<Box sx={{mt: 3, display: "flex", justifyContent: "flex-end"}}>
				<ConfirmButton
					title={t("creditors.merge.confirm.title")}
					cancel={t("actions.cancel")}
					onConfirm={handleMerge}
					variant="contained"
					color="warning"
					disabled={!target || merging}
				>
					{t("creditors.merge.confirm.message")}
				</ConfirmButton>
			</Box>
		</View>
	);
}

function CreditorDetails({creditor}: { creditor: Creditor }) {
	const {t} = useLocalization();
	return (
		<Grid container spacing={1}>
			<Grid item xs={12}>
				<LabeledText label={t("common.company")} text={creditor.company.companyName} />
			</Grid>
			{creditor.sapCreditorNumber && (
				<Grid item xs={12}>
					<LabeledText label={t("creditors.sapCreditorNumber")} text={creditor.sapCreditorNumber} />
				</Grid>
			)}
			{creditor.address && (
				<Grid item xs={12}>
					<LabeledText
						label={t("signup.companyAddress")}
						text={[creditor.address.street, creditor.address.houseNo, creditor.address.zip, creditor.address.city].filter(Boolean).join(", ")}
					/>
				</Grid>
			)}
		</Grid>
	);
}
